import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { useVideoPlatform } from '../hooks/useVideoPlatform';
import { participantSidsAtom, roomAtom, roomToDiagnosticInfo, useStoredAudioVideoSettings } from '../video-sdk';
import { isVirtualBackgroundSupportedAtom } from '../video-sdk/core/atoms';
import { gracefullySetLoggerGlobalContext } from './logger';

/**
 * Sets application-specific DataDog global context for logging i.e. all logs
 * will have this information. This should help debug with
 * application/user-specific context.
 *
 * You can add info to he global context as desired, but take care to not add
 * PHI/PII.
 */
export function useInitGlobalDDLoggerContext(additionalContext: Record<string, unknown> = {}) {
  const room = useAtomValue(roomAtom);
  const remoteParticipantSids = useAtomValue(participantSidsAtom);
  const storedAudioVideoSettings = useStoredAudioVideoSettings()?.storedAudioVideoSettings;
  const videoPlatform = useVideoPlatform();
  const isVirtualBackgroundSupported = useAtomValue(isVirtualBackgroundSupportedAtom);

  useEffect(
    function setGlobalLoggerDDContext() {
      (async () => {
        gracefullySetLoggerGlobalContext({
          storedAudioVideoSettings: storedAudioVideoSettings,
          ...(room
            ? {
                twilio: roomToDiagnosticInfo(room),
              }
            : {}),
          remoteParticipantSids,
          videoPlatform,
          isVirtualBackgroundSupported,
          ...additionalContext,
        });
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remoteParticipantSids, storedAudioVideoSettings, room, videoPlatform, isVirtualBackgroundSupported],
  );
}
